"use client"

import * as React from "react"
import { createRoot, type Root } from "react-dom/client"
import { CircleCheck, CircleX, Info, TriangleAlert, XIcon } from "lucide-react"

import { cn } from "@/custolink-newdesign/lib/utils"

/**
 * The new design's toast/notification stack.
 *
 * flow never installed a toast primitive (no sonner, no shadcn toast
 * registry file - checked `rg toast` and `rg sonner` in
 * custolink-flow/frontend_source, both empty besides a transitive
 * @radix-ui/react-toast nobody imports). `sonner` is not a dependency of
 * this app either (package.json has no entry), so rather than add one for a
 * handful of success/error banners, this is a small primitive built the same
 * way the rest of ui/ is: cva-free here (four fixed variants, no need), `cn`
 * for merging, `data-slot` for the Radix-portal token contract.
 *
 * SELF-MOUNTING, like the old `components/ui/toast/toast.tsx` it replaces.
 * That system never required a `<ToastWrapper>` placed anywhere - calling
 * `toast.push` the first time created its own root on `document.body`. This
 * does the same (`ensureMounted` below), rather than requiring a `<Toaster>`
 * in every shell. That matters here specifically: `useFormServerBacked` and
 * `useFormWrapper` are shared with pages outside any new-design shell (e.g.
 * `custolink-views/end_user/ResetPasswordForEndUserDesign.tsx`, gated by the
 * asset-frontend's own `useAssetFrontendDesign()`, not `isNewDesign()`). A
 * shell-mounted `<Toaster>` would silently drop their error toasts whenever
 * `isNewDesign()` is true but that page never rendered `AppShell`/`AuthShell`.
 * Self-mounting means `notify` works the same regardless of which shell, or
 * no shell, is in the tree.
 *
 * The viewport carries `data-slot="toast-viewport"`, added to the three
 * token-contract selector lists in `assets/styles/new-design/entry.css`
 * alongside the other portalled primitives (Dialog, Sheet, Select, ...) -
 * it mounts straight on `document.body`, outside `.shadcn-scope`, same as
 * those.
 */

export type ToastVariant = "success" | "danger" | "warning" | "info"

export interface ToastOptions {
  description?: React.ReactNode
  /** ms before auto-dismiss. 0 disables auto-dismiss. */
  duration?: number
}

interface ToastRecord {
  id: string
  variant: ToastVariant
  title: React.ReactNode
  description?: React.ReactNode
}

type Listener = () => void

let toasts: ToastRecord[] = []
const listeners = new Set<Listener>()

function emit() {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot() {
  return toasts
}

let seq = 0
function nextId() {
  seq += 1
  return `cl-toast-${seq}`
}

const DEFAULT_DURATION = 4500

function dismiss(id: string) {
  const next = toasts.filter((t) => t.id !== id)
  if (next.length !== toasts.length) {
    toasts = next
    emit()
  }
}

function dismissAll() {
  if (toasts.length === 0) return
  toasts = []
  emit()
}

function push(variant: ToastVariant, title: React.ReactNode, options?: ToastOptions) {
  ensureMounted()

  const id = nextId()
  const duration = options?.duration ?? DEFAULT_DURATION
  toasts = [...toasts, { id, variant, title, description: options?.description }]
  emit()

  if (duration > 0) {
    setTimeout(() => dismiss(id), duration)
  }

  return id
}

/** Drop-in replacement for the old `toast.push(<Notification .../>)` call sites. */
export const notify = {
  success: (title: React.ReactNode, description?: React.ReactNode) =>
    push("success", title, { description }),
  error: (title: React.ReactNode, description?: React.ReactNode) =>
    push("danger", title, { description }),
  warning: (title: React.ReactNode, description?: React.ReactNode) =>
    push("warning", title, { description }),
  info: (title: React.ReactNode, description?: React.ReactNode) =>
    push("info", title, { description }),
  dismiss,
  dismissAll,
}

const VARIANT_ICON: Record<ToastVariant, typeof CircleCheck> = {
  success: CircleCheck,
  danger: CircleX,
  warning: TriangleAlert,
  info: Info,
}

const VARIANT_ICON_CLASS: Record<ToastVariant, string> = {
  success: "cl:text-success",
  danger: "cl:text-destructive",
  warning: "cl:text-warning",
  info: "cl:text-info",
}

function ToastItem({ toast }: { toast: ToastRecord }) {
  const Icon = VARIANT_ICON[toast.variant]

  return (
    <div
      role="status"
      data-variant={toast.variant}
      className={cn(
        "cl:animate-in cl:fade-in-0 cl:slide-in-from-top-4 cl:pointer-events-auto cl:flex cl:w-full cl:items-start cl:gap-3 cl:rounded-lg cl:border cl:bg-card cl:p-4 cl:text-card-foreground cl:shadow-lg"
      )}
    >
      <Icon className={cn("cl:mt-0.5 cl:size-5 cl:shrink-0", VARIANT_ICON_CLASS[toast.variant])} />
      <div className="cl:flex cl:min-w-0 cl:flex-1 cl:flex-col cl:gap-1">
        <p className="cl:text-sm cl:leading-snug cl:font-medium">{toast.title}</p>
        {toast.description ? (
          <p className="cl:text-sm cl:leading-snug cl:text-muted-foreground">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        className="cl:text-muted-foreground cl:shrink-0 cl:rounded-xs cl:opacity-70 cl:transition-opacity cl:hover:opacity-100 cl:focus-visible:opacity-100 cl:focus-visible:outline-hidden cl:focus-visible:ring-2 cl:focus-visible:ring-ring"
        onClick={() => dismiss(toast.id)}
      >
        <XIcon className="cl:size-4" />
      </button>
    </div>
  )
}

function ToastViewport() {
  const items = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  return (
    <div
      data-slot="toast-viewport"
      aria-live="polite"
      aria-atomic="false"
      className="cl:pointer-events-none cl:fixed cl:top-4 cl:left-1/2 cl:z-[100] cl:flex cl:w-full cl:max-w-sm cl:-translate-x-1/2 cl:flex-col cl:gap-2 cl:px-4"
    >
      {items.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  )
}

let toastRoot: Root | null = null

/** Lazily mounts the single toast root on `document.body`, once per page load. */
function ensureMounted() {
  if (toastRoot || typeof document === "undefined") return

  const el = document.createElement("div")
  el.setAttribute("id", "cl-toast-root")
  document.body.appendChild(el)

  toastRoot = createRoot(el)
  toastRoot.render(<ToastViewport />)
}
